import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { ConfigPessoaService } from '../../../../shared/services/apis/pessoa/config-pessoa.service';
import { PessoaLookupService } from '../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ConfigPessoaRequest } from '../../../../shared/services/requests/pessoa/config-pessoa.request';
import { GetLookupPessoaResponse } from '../../../../shared/services/responses/pessoa/get-lookup-pessoa.response';
import { LookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';

@Component({
  selector: 'app-config-pessoa',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    LoadingBoxComponent,
    HeaderComponent,
    SelectPesquisavelComponent,
    NgxMaskDirective,
    NgxCurrencyDirective,
    MatProgressSpinnerModule,
    FooterActionComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './config-pessoa.component.html',
  styleUrls: ['./config-pessoa.component.css'],
})
export class ConfigPessoaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public form: FormGroup;
  public isLoading: boolean = true;
  public isSaving: boolean = false;

  public listaTipoPessoa: LookupItemStruct[] = [];
  public listaTipoContato: LookupItemStruct[] = [];
  public currencyMask = this.maskService.getCurrencyMask();

  private idConfigPessoa: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private configPessoaService: ConfigPessoaService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private maskService: MaskService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.carregarLookups();
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      isRestritoVendedor: [false],
      isVincularAutomaticamente: [false],
      limiteCreditoPadrao: [0],
      idTipoPessoa: [null],
      listIdTipoContato: [[]],
    });
  }

  private carregarLookups(): void {
    this.isLoading = true;
    this.pessoaLookupService.getLookup().subscribe({
      next: (res: GetLookupPessoaResponse) => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          this.isLoading = false;
          return;
        }

        // Assuming lookup returns these lists. If not present, we need to verify where they come from.
        // Based on previous files, GetLookupVendedorResponse usually has listaTipoPessoa, listaTipoContato.
        this.listaTipoPessoa = res.listaTipoPessoa || [];
        this.listaTipoContato = res.listaTipoContato || [];

        this.carregarConfiguracao();
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar as listas.', AlertType.error);
        console.error(err);
      },
    });
  }

  private carregarConfiguracao(): void {
    this.configPessoaService.get().subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        if (res.configPessoa) {
          this.idConfigPessoa = res.configPessoa.idConfigPessoa;
          this.form.patchValue({
            isRestritoVendedor: res.configPessoa.isRestritoVendedor,
            isVincularAutomaticamente: res.configPessoa.isVincularAutomaticamente,
            limiteCreditoPadrao: res.configPessoa.limiteCreditoPadrao,
            idTipoPessoa: res.configPessoa.idTipoPessoa,
            listIdTipoContato: res.listIdTipoContato, // Correctly mapped from response
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar as configurações.', AlertType.error);
        console.error(err);
      },
    });
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Verifique os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: ConfigPessoaRequest = this.form.value;

    this.configPessoaService.put(this.idConfigPessoa, request).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Configurações salvas com sucesso.', AlertType.success);
        }
      },
      error: err => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
        console.error(err);
      },
    });
  }

  public cancelar(): void {
    this.router.navigate(['/configuracoes/cadastros']);
  }

  public get idTipoPessoa(): FormControl {
    return this.form.get('idTipoPessoa') as FormControl;
  } // For Online Select
  public get listIdTipoContato(): FormControl {
    return this.form.get('listIdTipoContato') as FormControl;
  } // For Offline Select
}
